/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useEffect, useState } from 'react';
import mbpLogger from 'mbp-logger';
import { useSelector } from 'react-redux';
import { Route } from 'react-router-dom';
import { compose } from 'recompose';
import { func, shape, string } from 'prop-types';
import { getFeatureFlag } from '../../../../state/ducks/App/ducks/Config/Config-Selectors';

import Helper from '../state/reducers/Helper';
import { withAuth } from '../../../../state/ducks/Member/ducks/Auth/Plugin/auth';

const MAX_RETRIES = 5;
const RETRY_DELAY = 300;
/* eslint-disable react/jsx-props-no-spreading */
const ProtectedRoute = ({
    component: Component, auth, path, ...rest
}) => {
    const isPwaAuthCheckRetryOn = useSelector(getFeatureFlag(['is-pwa-auth-check-retry-on']));

    if (isPwaAuthCheckRetryOn) {
        const [authState, setAuthState] = useState({
            isAuthenticated: null,
            retries: 0,
            loading: true,
        });

        useEffect(() => {
            let retryTimeout;

            // Check authentication status
            const checkAuth = async () => {
                try {
                    // Get authentication details
                    const authentication = await Helper.getAuthentication();

                    if (authentication.isAuthenticated) {
                        // User is authenticated, update state and stop retries
                        setAuthState({
                            isAuthenticated: true,
                            retries: authState.retries,
                            loading: false,
                        });
                    } else if (authState.retries < MAX_RETRIES) {
                        // User is not authenticated, retry after delay
                        retryTimeout = setTimeout(() => {
                            setAuthState((prev) => ({
                                ...prev,
                                retries: prev.retries + 1,
                            }));
                        }, RETRY_DELAY);
                    } else {
                        // Max retries reached, stop checking and mark user as unauthenticated
                        setAuthState({
                            isAuthenticated: false,
                            retries: MAX_RETRIES,
                            loading: false,
                        });
                    }
                } catch (error) {
                    // Log any errors that occur during authentication check
                    setAuthState({
                        isAuthenticated: false,
                        retries: MAX_RETRIES,
                        loading: false,
                    });
                    mbpLogger.logError({
                        function: 'ProtectedRoute.js',
                        message: 'Error while checking isAuthenticated status',
                        appName: process.env.npm_package_name,
                        module: 'Account',
                        error,
                    });
                }
            };

            // Start auth check
            checkAuth();

            return () => {
                // Clean up timeout if component unmounts
                if (retryTimeout) clearTimeout(retryTimeout);
            };
        }, [authState.retries]);

        useEffect(() => {
            if (authState.isAuthenticated === false && authState.loading === false) {
                auth.loginByRedirect({}, { routeBack: path });
            }
        }, [authState.isAuthenticated, authState.loading, auth, path]);

        if (authState.loading) {
            return <></>;
        }

        return authState.isAuthenticated ? (
            <Route
                {...rest}
                exact
                render={(props) => <Component {...props} />}
            />
        ) : null; // Return null while redirecting inside useEffect
    }

    const { isAuthenticated } = Helper.getAuthentication();

    return (
        <Route
            {...rest}
            exact
            render={(props) => {
                if (!isAuthenticated) {
                    auth.loginByRedirect({}, { routeBack: path });
                }
                return <Component {...props} />;
            }}
        />
    );
};

ProtectedRoute.propTypes = {
    component: func.isRequired,
    auth: shape({
        loginByRedirect: func.isRequired,
    }).isRequired,
    path: string.isRequired,
};

const enhance = compose(
    withAuth,
);

export default enhance(ProtectedRoute);
